import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

const STORAGE_KEY = 'cookie_consent';
const POLICY_VERSION = '1';

function readStoredConsent() {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    const choice = String(parsed.choice || '').toLowerCase();
    if (choice !== 'accepted' && choice !== 'rejected') return null;
    return {
      choice,
      updatedAt: String(parsed.updatedAt || ''),
      policyVersion: String(parsed.policyVersion || ''),
    };
  } catch (e) {
    return null;
  }
}

function writeStoredConsent(choice) {
  const payload = {
    choice,
    updatedAt: new Date().toISOString(),
    policyVersion: POLICY_VERSION,
  };
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  return payload;
}

const CookieConsentBanner = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [consent, setConsent] = useState(null);

  useEffect(() => {
    const stored = readStoredConsent();
    setConsent(stored);
    setIsOpen(!stored);
  }, []);

  useEffect(() => {
    const onOpen = () => {
      const stored = readStoredConsent();
      setConsent(stored);
      setIsOpen(true);
    };

    window.addEventListener('tripod:open-cookie-settings', onOpen);
    return () => {
      window.removeEventListener('tripod:open-cookie-settings', onOpen);
    };
  }, []);

  const handleChoice = useCallback((choice) => {
    try {
      const stored = writeStoredConsent(choice);
      setConsent(stored);
    } finally {
      setIsOpen(false);
    }
  }, []);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-x-0 bottom-0 z-50 border-t border-gray-200 bg-white/95 backdrop-blur"
      role="region"
      aria-label="Cookie consent"
    >
      <div className="mx-auto flex max-w-7xl flex-col gap-3 px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:gap-4 sm:px-6 lg:px-8">
        <div className="min-w-0">
          <div className="text-sm font-semibold text-gray-900">Cookie preferences</div>
          <div className="mt-1 text-sm text-gray-600">
            We use cookies to improve your experience. You can accept or reject non-essential cookies.{' '}
            <Link to="/privacy" className="text-purple-700 underline underline-offset-2 hover:text-purple-800">
              Privacy Policy
            </Link>
            {consent?.choice ? (
              <span className="ml-2 text-xs text-gray-500">Current: {consent.choice}</span>
            ) : null}
          </div>
        </div>

        <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:items-center">
          <button
            type="button"
            onClick={() => handleChoice('rejected')}
            className="inline-flex min-h-[44px] items-center justify-center rounded-lg border border-gray-300 bg-white px-4 text-sm font-semibold text-gray-900 transition-colors hover:bg-gray-50"
          >
            Reject
          </button>
          <button
            type="button"
            onClick={() => handleChoice('accepted')}
            className="inline-flex min-h-[44px] items-center justify-center rounded-lg bg-purple-600 px-4 text-sm font-semibold text-white transition-colors hover:bg-purple-700"
          >
            Accept
          </button>
        </div>
      </div>
    </div>
  );
};

export default CookieConsentBanner;
